/* DORTI free pick offer — markup in snippets/dorti-free-pick.liquid.
 *
 * THE COUNTDOWN IS REAL. That is the design constraint everything here
 * follows from. A timer that restarts on every refresh is the textbook fake
 * urgency regulators go after (FTC dark-patterns report; EU UCPD Annex I §7),
 * and shoppers who notice it stop believing the shop. So:
 *
 *   - the clock starts the first time the card itself is on screen (or is
 *     ticked), not on page load and not merely when the drawer opens;
 *   - the deadline is stored on the device and survives reloads and tabs;
 *   - when it runs out, the offer is withdrawn — an unclaimed one disappears,
 *     a claimed pick is taken back out of the cart — and it does not come back
 *     on that device until the cool-down set in Theme settings has passed;
 *   - one exception, in the shopper's favour: if they pressed Check out while
 *     the clock was running with the pick in the cart, they did what was
 *     asked, and the pick stays even if they wander back after the deadline.
 *
 * Without browser storage there is nowhere honest to keep a deadline, so no
 * offer is shown at all.
 *
 * CART CHANGES ONLY HAPPEN WITH THE CART ON SCREEN. Broadcast's cart refresh always ends by
 * opening the cart drawer, so anything that edits the cart from here (taking
 * back an expired pick, removing a pick left on its own, trimming two picks to
 * one) waits until the offer's spot is visible. Otherwise a timer running out
 * while someone browses would throw the drawer open at them.
 *
 * The element is re-created from the server after every cart change (it is
 * rendered at the end of the cart lines, which the theme re-renders), so it
 * holds no state of its own beyond what it reads from the markup and from
 * storage.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('dorti-free-pick')) return;

  const STATE_KEY = 'dorti:free-pick';
  const ENDED_KEY = 'dorti:free-pick:ended';
  const DAY = 86400000;

  const probe = (area) => {
    try {
      const s = window[area];
      s.setItem('dorti:probe', '1');
      s.removeItem('dorti:probe');
      return s;
    } catch (e) {
      return null;
    }
  };
  const store = probe('localStorage');
  const session = probe('sessionStorage');

  const readState = () => {
    if (!store) return null;
    try {
      const s = JSON.parse(store.getItem(STATE_KEY));
      return s && Number.isFinite(s.start) && Number.isFinite(s.deadline) ? s : null;
    } catch (e) {
      return null;
    }
  };
  const writeState = (s) => {
    try {
      store.setItem(STATE_KEY, JSON.stringify(s));
    } catch (e) {}
  };
  const clearState = () => {
    try {
      store.removeItem(STATE_KEY);
    } catch (e) {}
  };
  const endedThisSession = () => !!(session && session.getItem(ENDED_KEY));
  const markEnded = () => {
    try {
      if (session) session.setItem(ENDED_KEY, '1');
    } catch (e) {}
  };

  const phaseOf = (s, now) => {
    if (!s) return 'fresh';
    if (now < s.deadline) return 'running';
    if (s.lockedAt && s.lockedAt <= s.deadline) return 'locked';
    return 'expired';
  };

  const routes = () => (window.theme && window.theme.routes) || {};

  const post = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {}
    if (!res.ok) {
      const err = new Error((data && (data.description || data.message)) || 'The cart did not accept that.');
      err.status = res.status;
      throw err;
    }
    return data;
  };

  const addPick = (variantId) =>
    post(`${routes().cart_add_url || '/cart/add'}.js`, { items: [{ id: Number(variantId), quantity: 1 }] });

  const setLine = (key, quantity) => post(`${routes().cart_change_url || '/cart/change'}.js`, { id: key, quantity });

  // Broadcast re-renders the drawer / cart page — and this offer with it — on this.
  const refreshCart = () => document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

  // One cart request at a time, across re-renders of the element.
  let busy = false;

  /* Check out pressed while the clock is running, with the pick in the cart:
     they did what the offer asked. Listened for on the document in the capture
     phase so it is recorded before the page starts navigating. */
  const lockIfClaimed = () => {
    const s = readState();
    if (!s || Date.now() >= s.deadline) return;
    const el = document.querySelector('dorti-free-pick');
    if (!el || Number(el.dataset.lineQty || 0) < 1) return;
    s.lockedAt = Date.now();
    writeState(s);
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const action = (form && form.getAttribute && form.getAttribute('action')) || '';
      if ((event.submitter && event.submitter.name === 'checkout') || /\/checkout/.test(action)) lockIfClaimed();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const t = event.target;
      if (
        t &&
        t.closest &&
        t.closest(
          '[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart, shopify-accelerated-checkout'
        )
      ) {
        lockIfClaimed();
      }
    },
    true
  );

  class DortiFreePick extends HTMLElement {
    connectedCallback() {
      if (!store) return;

      this.variantId = this.dataset.variantId;
      this.lineKey = this.dataset.lineKey || '';
      this.lineQty = Number(this.dataset.lineQty || 0);
      this.paid = Number(this.dataset.paid || 0);
      this.available = this.dataset.available === 'true';
      this.minutes = Math.max(1, Number(this.dataset.minutes) || 15);
      this.cooldown = Math.max(1, Number(this.dataset.cooldownDays) || 7) * DAY;

      this.checkbox = this.querySelector('[data-fp-checkbox]');
      this.clock = this.querySelector('[data-fp-clock]');
      this.timerEl = this.querySelector('[data-fp-timer]');
      this.tagEl = this.querySelector('[data-fp-tag]');
      this.yesEl = this.querySelector('[data-fp-yes]');
      this.note = this.querySelector('[data-fp-note]');
      this.tagText = this.tagEl ? this.tagEl.textContent : '';
      this.yesText = this.yesEl ? this.yesEl.textContent : '';

      this.onToggle = this.onToggle.bind(this);
      if (this.checkbox) this.checkbox.addEventListener('change', this.onToggle);

      // Draw what is already known straight away: this runs inside the
      // theme's re-render, before the browser paints, so a cart refresh never
      // makes the offer blink out and back. Drawing never edits the cart and
      // never starts a clock — evaluate() does those, on screen only.
      this.state = this.currentState(Date.now());
      this.paint();

      // Two things to watch. The list it sits in (the open drawer, the cart
      // page's lines) decides when the cart may be tidied — a hidden offer
      // never intersects, so it cannot be its own signal. The card itself
      // decides when the clock starts: it lives in a scrolling list, below
      // the lines, so the drawer being open is not the same as the offer
      // having been seen.
      const list = this.parentElement || this;
      this.io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.target === this) this.seen = e.isIntersecting && e.intersectionRatio >= 0.5;
            else this.listVisible = e.isIntersecting;
          }
          const visible = !!(this.listVisible || this.seen);
          const worthALook = (visible && !this.visible) || (this.seen && !this.state);
          this.visible = visible;
          if (worthALook) this.evaluate();
        },
        { threshold: [0, 0.5] }
      );
      this.io.observe(list);
      if (list !== this) this.io.observe(this);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      clearInterval(this.ticker);
      if (this.checkbox) this.checkbox.removeEventListener('change', this.onToggle);
    }

    // The stored window, unless its cool-down has passed — then a new one may open.
    currentState(now) {
      const s = readState();
      if (s && now > s.deadline + this.cooldown) {
        clearState();
        return null;
      }
      return s;
    }

    // On screen: tidy the cart if it needs it, start the clock if none is running.
    evaluate() {
      if (busy) return;
      const now = Date.now();

      // "With their purchase": a pick on its own is not an order.
      if (this.paid === 0) {
        if (this.lineQty > 0) this.update(0);
        return;
      }
      // One per order.
      if (this.lineQty > 1) {
        this.update(1);
        return;
      }

      this.state = this.currentState(now);
      if (!this.state && this.available && this.seen) this.startWindow(now);
      this.paint();
    }

    startWindow(now) {
      this.state = { start: now, deadline: now + this.minutes * 60000, lockedAt: 0 };
      writeState(this.state);
    }

    paint() {
      clearInterval(this.ticker);
      const now = Date.now();
      const phase = phaseOf(this.state, now);
      const inCart = this.lineQty > 0;

      if (this.paid === 0) {
        this.hidden = true;
        return;
      }

      if (phase === 'fresh') {
        // Not seen yet: the offer shows its full time, standing still. The
        // clock starts once the card itself is on screen, or on the tick.
        if (!this.available && !inCart) {
          this.hidden = true;
          return;
        }
        this.classList.remove('is-ended', 'is-locked', 'is-urgent');
        this.classList.toggle('is-claimed', inCart);
        this.tagEl.textContent = this.tagText;
        this.timerEl.hidden = false;
        this.clock.textContent = `${this.minutes}:00`;
        this.yesEl.textContent = inCart ? 'Added — free with this order' : this.yesText;
        this.hidden = false;
        return;
      }

      if (phase === 'expired') {
        if (inCart) {
          // The deadline passed before checkout: take the pick back out —
          // on screen, where the shopper can see why.
          this.hidden = true;
          if (this.visible) {
            markEnded();
            this.update(0);
          }
          return;
        }
        if (this.watched && this.visible) markEnded();
        if (endedThisSession()) this.showEnded();
        else this.hidden = true;
        return;
      }

      this.classList.remove('is-ended');

      if (phase === 'locked') {
        // A lock keeps a pick that is in the cart; it does not re-open the offer.
        if (!inCart) {
          this.hidden = true;
          return;
        }
        this.classList.add('is-locked', 'is-claimed');
        this.tagEl.textContent = 'Free pick locked in';
        this.timerEl.hidden = true;
        this.yesEl.textContent = 'It ships with this order';
        this.show();
        return;
      }

      // running
      if (!inCart && !this.available) {
        this.hidden = true;
        return;
      }
      this.classList.remove('is-locked');
      this.classList.toggle('is-claimed', inCart);
      this.tagEl.textContent = this.tagText;
      this.timerEl.hidden = false;
      this.yesEl.textContent = inCart ? 'Added — free with this order' : this.yesText;
      this.tick();
      this.ticker = setInterval(() => this.tick(), 1000);
      this.watched = true;
      this.show();
    }

    tick() {
      const left = this.state.deadline - Date.now();
      if (left <= 0) {
        this.paint();
        return;
      }
      const total = Math.ceil(left / 1000);
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      this.clock.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
      this.classList.toggle('is-urgent', total <= 60);
    }

    show() {
      this.hidden = false;
    }

    showEnded() {
      this.classList.remove('is-claimed', 'is-locked', 'is-urgent');
      this.classList.add('is-ended');
      this.note.textContent = 'The free pick offer has ended.';
      this.note.hidden = false;
      this.hidden = false;
    }

    async onToggle() {
      const want = this.checkbox.checked;
      if (busy) {
        this.checkbox.checked = !want;
        return;
      }
      const phase = phaseOf(this.state, Date.now());
      // Ticking it is as sure a sign of having seen it as any.
      if (want && phase === 'fresh' && this.available) this.startWindow(Date.now());
      else if (want && phase !== 'running') {
        this.checkbox.checked = false;
        this.paint();
        return;
      }

      busy = true;
      this.classList.add('is-busy');
      this.note.hidden = true;
      try {
        if (want) await addPick(this.variantId);
        else await setLine(this.lineKey, 0);
        refreshCart();
      } catch (err) {
        this.checkbox.checked = !want;
        this.note.textContent =
          want && err.status === 422 ? 'The free picks just ran out — sorry.' : 'That didn’t go through. Try again.';
        this.note.hidden = false;
      } finally {
        busy = false;
        this.classList.remove('is-busy');
      }
    }

    // Housekeeping on the pick's own line. Only ever called while on screen.
    async update(quantity) {
      if (busy || !this.lineKey) return;
      busy = true;
      try {
        await setLine(this.lineKey, quantity);
        refreshCart();
      } catch (e) {
        // Leave it; the next time the offer is on screen it tries again.
      } finally {
        busy = false;
      }
    }
  }

  window.customElements.define('dorti-free-pick', DortiFreePick);
})();
